import { validAuthFactors } from '../constants/clientConstants';

const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const CACHE_NAME = 'vf-mediapipe-v1';
const CHUNK_SIZE = 1024 * 1024; // 1 MiB Range chunks for the large wasm

const PRELOAD_FLAG = '__sbiLivenessPreloadStarted';
const WARM_PROMISE = '__sbiLivenessWarmPromise';

export function getOidcUiAssetBase() {
  if (typeof window === 'undefined') {
    return publicBase;
  }
  return `${window.location.origin}${publicBase}`;
}

export function getLocalMediapipeBase() {
  return `${getOidcUiAssetBase()}/mediapipe`;
}

export const SBI_LIVENESS_MODEL_URL = `${publicBase}/mediapipe/face_landmarker.task`;
export const SBI_LIVENESS_WASM_BASE = `${publicBase}/mediapipe/wasm`;
export const SBI_LIVENESS_MEDIAPIPE_CDN = getLocalMediapipeBase();

const parseErshaClientIds = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id).trim()).filter(Boolean);
  }

  const text = String(raw ?? '').trim();
  if (!text) {
    return [];
  }

  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((id) => String(id).trim()).filter(Boolean);
      }
    } catch {
      // fall through to comma-separated parsing
    }
  }

  return text
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

const getErshaClientIds = () => {
  const runtime = window._env_?.ERSHA_CLIENT_IDS;
  if (runtime !== undefined && runtime !== null && runtime !== '') {
    return parseErshaClientIds(runtime);
  }
  return parseErshaClientIds(process.env.REACT_APP_ERSHA_CLIENT_IDS ?? '');
};

export const isErshaClientId = (clientId) => {
  if (!clientId) {
    return false;
  }
  const allowedClientIds = getErshaClientIds();
  if (allowedClientIds.length === 0) {
    return false;
  }
  return allowedClientIds.includes(String(clientId).trim());
};

const hasPreloadLink = (href) =>
  Boolean(document.querySelector(`link[data-sbi-preload="${href}"]`));

const appendPreloadLink = ({ href, as }) => {
  if (!href || hasPreloadLink(href)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = href;
  link.as = as;
  link.crossOrigin = 'anonymous';
  link.setAttribute('data-sbi-preload', href);
  document.head.appendChild(link);
};

const contentTypeFor = (url) => {
  if (url.endsWith('.wasm')) return 'application/wasm';
  if (url.endsWith('.mjs') || url.endsWith('.js'))
    return 'application/javascript';
  if (url.endsWith('.task')) return 'application/octet-stream';
  return 'application/octet-stream';
};

async function fetchChunkedArrayBuffer(url) {
  let total = 0;
  let acceptsRanges = false;
  try {
    const head = await fetch(url, {
      method: 'HEAD',
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
    });
    total = Number(head.headers.get('content-length') || 0);
    acceptsRanges = /bytes/i.test(head.headers.get('accept-ranges') || '');
  } catch {
    acceptsRanges = false;
  }

  if (!acceptsRanges || !total || total <= CHUNK_SIZE) {
    const res = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch ${url}: ${res.status}`);
    }
    return res.arrayBuffer();
  }

  const chunks = [];
  for (let start = 0; start < total; start += CHUNK_SIZE) {
    const end = Math.min(start + CHUNK_SIZE - 1, total - 1);
    const res = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      headers: { Range: `bytes=${start}-${end}` },
      cache: 'force-cache',
    });
    if (!(res.ok || res.status === 206)) {
      throw new Error(
        `Chunk fetch failed ${url} ${start}-${end}: ${res.status}`
      );
    }
    chunks.push(new Uint8Array(await res.arrayBuffer()));
  }

  const merged = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return merged.buffer;
}

async function putUrlInCache(cache, url) {
  const existing = await cache.match(url);
  if (existing) {
    return;
  }

  if (url.endsWith('.wasm')) {
    const buffer = await fetchChunkedArrayBuffer(url);
    await cache.put(
      url,
      new Response(buffer, {
        status: 200,
        headers: {
          'Content-Type': contentTypeFor(url),
          'Content-Length': String(buffer.byteLength),
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
      })
    );
    return;
  }

  const res = await fetch(url, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'force-cache',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${url}: ${res.status}`);
  }
  await cache.put(url, res.clone());
}

async function ensureMediapipeServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  try {
    await navigator.serviceWorker.register(`${publicBase}/mediapipe-sw.js`, {
      scope: `${publicBase}/` || '/',
    });
  } catch (err) {
    console.warn('[sbi-preload] service worker register failed', err);
  }
}

/**
 * Download MediaPipe assets from this UI host (chunked for the large wasm)
 * and keep them in Cache Storage for the liveness iframe.
 */
export async function warmSbiLivenessCache() {
  if (typeof window === 'undefined') {
    return;
  }
  if (window[WARM_PROMISE]) {
    return window[WARM_PROMISE];
  }

  window[WARM_PROMISE] = (async () => {
    await ensureMediapipeServiceWorker();
    const mp = getLocalMediapipeBase();
    const urls = [
      `${mp}/vision_bundle.js`,
      `${mp}/face_landmarker.task`,
      `${mp}/wasm/vision_wasm_internal.js`,
      `${mp}/wasm/vision_wasm_internal.wasm`,
      `${mp}/wasm/vision_wasm_nosimd_internal.js`,
      `${mp}/wasm/vision_wasm_nosimd_internal.wasm`,
    ];

    for (const href of urls) {
      appendPreloadLink({
        href,
        as: href.endsWith('.js') || href.endsWith('.mjs') ? 'script' : 'fetch',
      });
    }

    if (!('caches' in window)) {
      await Promise.all(
        urls.map((url) => fetch(url, { cache: 'force-cache' }).catch(() => {}))
      );
      return;
    }

    const cache = await caches.open(CACHE_NAME);
    await Promise.all(
      urls.map((url) =>
        putUrlInCache(cache, url).catch((err) => {
          console.warn('[sbi-preload] warm failed', url, err);
        })
      )
    );
  })();

  return window[WARM_PROMISE];
}

/**
 * Start warming as soon as login / biometrics is shown.
 */
export function preloadSbiLivenessAssets() {
  if (typeof window === 'undefined' || window[PRELOAD_FLAG]) {
    return;
  }
  window[PRELOAD_FLAG] = true;
  warmSbiLivenessCache();
}

export function preloadSbiLivenessForLogin(openIDConnectService, authService) {
  const clientId = authService?.getClientId?.();
  if (!isErshaClientId(clientId)) {
    return;
  }

  const authFactors = openIDConnectService?.getAuthFactorList?.() ?? [];
  const hasBio = authFactors.some(
    (factor) => factor.type === validAuthFactors.BIO
  );

  if (authFactors.length > 0 && !hasBio) {
    return;
  }

  preloadSbiLivenessAssets();
}
